<script lang="ts">
	// UC-39: a shop's tables (D23), beside its delivery areas in Shops.
	//
	// A table is only a name, such as T5 or Terrace 2. The list comes from the
	// server in name order, "T2" before "T10", and is read again after every
	// change rather than sorted here. A name is changed in place; a new table
	// is a blank row at the end. The rules are the server's: a name of 1 to 40
	// characters once trimmed, unique in the shop whatever its case, at most
	// 200 tables, and each refusal shows in place, in the owner's words.
	//
	// Removing a table is undone, not confirmed: it leaves the list at once and
	// the server hears of it when the Undo runs out (owner/notices.svelte.ts).
	// The tills stop offering it then, and an order at it, open or paid, keeps
	// it. Until then the list leaves it out of every read, so a read another
	// change starts never shows it again, and an Undo puts it back once.

	import Button from '../components/Button.svelte';
	import Card from '../components/Card.svelte';
	import Field from '../components/Field.svelte';
	import Loading from '../components/Loading.svelte';
	import { api } from '../api/client';
	import { fill, message, type Language } from '../i18n/messages';
	import { flash, removeWithUndo } from '../owner/notices.svelte';
	import type { components } from '../api/types';

	type Table = components['schemas']['TableBody'];

	interface Props {
		language: Language;
		shopId: string;
	}

	let { language, shopId }: Props = $props();

	let tables = $state<Table[]>([]);
	let loaded = $state(false);
	// A read that failed, which says so rather than show an empty shop.
	let unread = $state(false);
	// Tables whose removal waits for its Undo: hidden from every read.
	let hidden = $state<string[]>([]);
	let edit = $state<{ id: string; name: string; error: string } | null>(null);
	let busy = $state(false);
	let full = $state(false);
	let failure = $state('');

	// The list follows the shop on screen. A shop's first read shows the wait;
	// a read after a change keeps the list in place until it answers. Reads
	// can overlap, and only the latest one asked replaces the list.
	let readFor = '';
	let asked = 0;
	$effect(() => {
		if (shopId === readFor) return;
		readFor = shopId;
		edit = null;
		hidden = [];
		full = false;
		failure = '';
		loaded = false;
		void load(shopId);
	});

	async function load(shop: string) {
		const ask = ++asked;
		let read: Table[] | null = null;
		try {
			const { data } = await api.GET('/api/owner/shops/{shopId}/tables', {
				params: { path: { shopId: shop } },
			});
			read = data?.tables ?? null;
		} catch {
			// The read failed: said below, with Retry.
		}
		if (shop !== readFor || ask !== asked) return;
		unread = read === null;
		tables = (read ?? []).filter((t) => !hidden.includes(t.id));
		loaded = true;
	}

	function startNew() {
		full = false;
		failure = '';
		edit = { id: 'new', name: '', error: '' };
	}

	async function save() {
		const current = edit;
		if (!current || !current.name.trim() || busy) return;
		busy = true;
		failure = '';
		const body = { name: current.name.trim() };
		try {
			const result =
				current.id === 'new'
					? await api.POST('/api/owner/shops/{shopId}/tables', {
							params: { path: { shopId } },
							body,
						})
					: await api.PATCH('/api/owner/shops/{shopId}/tables/{tableId}', {
							params: { path: { shopId, tableId: current.id } },
							body,
						});
			if (result.error !== undefined || !result.data) {
				const code = result.error?.code ?? '';
				if (code === 'TABLE_LIMIT') {
					full = true;
					edit = null;
				} else if (code === 'TABLE_NAME_IN_USE') {
					current.error = message(language, 'tableNameInUse');
				} else if (code === 'VALIDATION_ERROR') {
					current.error = message(language, 'tableNameBounds');
				} else {
					failure = result.error?.message ?? message(language, 'loadFailed');
					edit = null;
					await load(shopId);
				}
				return;
			}
			edit = null;
			flash(message(language, 'saved'));
			await load(shopId);
		} catch {
			failure = message(language, 'loadFailed');
		} finally {
			busy = false;
		}
	}

	function remove(table: Table) {
		const shop = shopId;
		full = false;
		hidden = [...hidden, table.id];
		tables = tables.filter((t) => t.id !== table.id);
		const unhide = () => (hidden = hidden.filter((id) => id !== table.id));
		removeWithUndo(
			fill(message(language, 'tablesRemoved'), table.name),
			async () => {
				try {
					await api.POST('/api/owner/shops/{shopId}/tables/{tableId}/remove', {
						params: { path: { shopId: shop, tableId: table.id } },
					});
				} finally {
					unhide();
					if (shop === readFor) await load(shop);
				}
			},
			() => {
				// The server never heard of it: read the list again, which holds
				// it in its place.
				unhide();
				if (shop === readFor) void load(shop);
			},
		);
	}
</script>

<Card title={message(language, 'tablesTitle')} testId="tables-{shopId}">
	<div class="rules">
		<p>{message(language, 'tablesRuleWhat')}</p>
		<p>{message(language, 'tablesNote')}</p>
		<p>{message(language, 'tablesRuleSeveral')}</p>
	</div>
	{#if !loaded}
		<div class="wait">
			<Loading
				label={message(language, 'tablesLoading')}
				caption={message(language, 'tablesLoading')}
			/>
		</div>
	{:else}
		{#if unread}
			<div class="unread" role="alert" data-testid="tables-unread">
				<p class="empty">{message(language, 'tablesLoadFailed')}</p>
				<Button
					testId="tables-retry"
					label={message(language, 'retry')}
					tone="secondary"
					size="sm"
					onPress={() => void load(shopId)}
				/>
			</div>
		{:else if tables.length === 0 && edit?.id !== 'new'}
			<p class="empty" data-testid="tables-empty">{message(language, 'tablesEmpty')}</p>
		{/if}
		{#each [...tables, ...(edit?.id === 'new' ? [{ id: 'new', name: '' }] : [])] as table (table.id)}
			<div class="row" data-testid="table-{table.id}">
				{#if edit && edit.id === table.id}
					<div class="name-field">
						<Field
							testId="table-name"
							ariaLabel={message(language, 'tablesName')}
							placeholder={message(language, 'tablesNameHint')}
							value={edit.name}
							maxLength={200}
							error={edit.error || undefined}
							errorTestId="table-name-error"
							onInput={(v) => {
								if (edit) {
									edit.name = v;
									edit.error = '';
								}
							}}
						/>
					</div>
					<Button
						testId="table-save"
						label={message(language, 'save')}
						size="sm"
						state={busy ? 'pending' : 'idle'}
						pendingLabel={message(language, 'saving')}
						disabled={!edit.name.trim()}
						onPress={() => void save()}
					/>
					<Button
						testId="table-cancel"
						label={message(language, 'cancel')}
						tone="text-quiet"
						size="sm"
						onPress={() => (edit = null)}
					/>
				{:else}
					<span class="label"><bdi>{table.name}</bdi></span>
					<Button
						testId="table-edit-{table.id}"
						label={message(language, 'edit')}
						tone="text"
						size="sm"
						disabled={edit !== null}
						onPress={() => {
							full = false;
							edit = { id: table.id, name: table.name, error: '' };
						}}
					/>
					<Button
						testId="table-remove-{table.id}"
						label={message(language, 'remove')}
						tone="text-quiet"
						size="sm"
						disabled={edit !== null}
						onPress={() => remove(table)}
					/>
				{/if}
			</div>
		{/each}
	{/if}
	<div class="add">
		{#if full}
			<p class="said" role="alert" data-testid="tables-full">{message(language, 'tableLimit')}</p>
		{/if}
		{#if failure}
			<p class="said" role="alert" data-testid="tables-failure">{failure}</p>
		{/if}
		<Button
			testId="table-add"
			icon="plus"
			label={message(language, 'tablesAdd')}
			tone="secondary"
			size="sm"
			disabled={edit !== null || !loaded || unread}
			onPress={startNew}
		/>
	</div>
</Card>

<style>
	.rules {
		display: flex;
		flex-direction: column;
		gap: var(--space-2);
		padding: var(--row-pad);
		font-size: var(--text-2xs);
		line-height: var(--leading-snug);
		color: var(--text-muted);
		border-block-end: var(--border-width) solid var(--border-inset);
		text-wrap: pretty;
	}

	.rules p {
		margin: 0;
	}

	.wait {
		display: flex;
		justify-content: center;
		padding-block: var(--space-9);
	}

	.empty {
		margin: 0;
		padding: var(--card-pad);
		font-size: var(--text-sm);
		line-height: var(--leading-snug);
		color: var(--text-body);
	}

	.unread {
		display: flex;
		flex-direction: column;
		align-items: flex-start;
		gap: var(--space-3);
		padding: 0 var(--card-pad) var(--card-pad);
	}

	.unread .empty {
		padding: var(--card-pad) 0 0;
	}

	.row {
		display: flex;
		flex-wrap: wrap;
		align-items: center;
		gap: var(--space-5);
		padding: var(--row-pad);
		border-block-end: var(--border-width) solid var(--border-inset);
	}

	.label {
		flex: 1;
		min-inline-size: 0;
		font-size: var(--text-sm);
		font-weight: 600;
		overflow-wrap: anywhere;
	}

	.name-field {
		flex: 1 1 160px;
		min-inline-size: 0;
	}

	.add {
		display: flex;
		flex-direction: column;
		align-items: flex-start;
		gap: var(--space-3);
		padding: var(--row-pad);
	}

	.said {
		margin: 0;
		font-size: var(--text-2xs);
		font-weight: 600;
		color: var(--ink-flag-deep);
	}
</style>
